import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, parseISO, startOfWeek, endOfWeek, addDays, subDays } from 'date-fns';
import { 
  FileSpreadsheet, Download, Calendar as CalendarIcon, 
  Loader2, Clock, User, ChevronLeft, ChevronRight, AlertTriangle,
  CheckCircle, XCircle, Building, FileText, Search, X, TrendingUp,
  TrendingDown, Timer, Users, Filter, Edit
} from 'lucide-react';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';

// Quick Filter Chip Component
const FilterChip = ({ label, active, onClick, count }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
      active 
        ? 'bg-blue-600 text-white shadow-md' 
        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
    }`}
  >
    {label}
    {count !== undefined && (
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
        active ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-500'
      }`}>
        {count}
      </span>
    )}
  </button>
);

// Timesheet Row Card
const TimesheetCard = ({ row, onApprove, onReject, processingId }) => {
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustedClockIn, setAdjustedClockIn] = useState('');
  const [adjustedClockOut, setAdjustedClockOut] = useState('');
  
  const variance = (row.actual_hours || 0) - (row.scheduled_hours || 0);
  const isLate = row.is_late;
  const isPending = row.approval_status === 'pending';
  
  const handleApproveWithAdjustment = () => {
    onApprove(row.attendance_id, {
      adjusted_clock_in: adjustedClockIn || null,
      adjusted_clock_out: adjustedClockOut || null,
    });
  };
  
  return (
    <div 
      className={`p-4 rounded-xl border transition-all hover:shadow-md ${
        isLate && isPending 
          ? 'bg-amber-50 border-amber-200' 
          : 'bg-white border-slate-200'
      }`}
      data-testid={`timesheet-card-${row.id || row.date}`}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Guard Info */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
            {row.guard_name?.[0]?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="font-semibold text-slate-800">{row.guard_name || 'Unknown'}</p>
            <p className="text-xs text-slate-500">
              {row.date ? format(parseISO(row.date), 'EEEE, MMM d') : 'No date'}
            </p>
          </div>
        </div>
        
        {/* Right: Status */}
        <div className="flex items-center gap-2">
          {isLate && (
            <Badge variant="outline" className={`text-xs ${
              row.approval_status === 'pending' ? 'bg-amber-100 text-amber-700 border-amber-300' :
              row.approval_status === 'approved' ? 'bg-blue-100 text-blue-700 border-blue-300' :
              'bg-red-100 text-red-700 border-red-300'
            }`}>
              {row.late_minutes || '?'} min late
            </Badge>
          )}
          {row.status === 'completed' && !isLate && (
            <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
              <CheckCircle className="w-3 h-3 mr-1" /> Complete
            </Badge>
          )}
        </div>
      </div>
      
      {/* Time Details */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Scheduled</p>
          <p className="text-sm font-semibold text-slate-700">
            {row.scheduled_start && row.scheduled_end 
              ? `${row.scheduled_start} - ${row.scheduled_end}`
              : '—'}
          </p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Actual</p>
          <p className={`text-sm font-semibold ${isLate ? 'text-amber-600' : 'text-slate-700'}`}>
            {row.actual_start 
              ? `${row.actual_start} - ${row.actual_end || 'ongoing'}`
              : '—'}
          </p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Hours</p>
          <p className="text-sm font-semibold text-slate-700">
            {row.actual_hours?.toFixed(1) || '0'} / {row.scheduled_hours?.toFixed(1) || '0'}
          </p>
        </div>
        <div className={`rounded-lg p-2.5 ${
          variance > 0 ? 'bg-green-50' : variance < 0 ? 'bg-red-50' : 'bg-slate-50'
        }`}>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Variance</p>
          <p className={`text-sm font-semibold flex items-center gap-1 ${
            variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : 'text-slate-500'
          }`}>
            {variance > 0 && <TrendingUp className="w-3 h-3" />}
            {variance < 0 && <TrendingDown className="w-3 h-3" />}
            {variance !== 0 ? (variance > 0 ? '+' : '') + variance.toFixed(1) + 'h' : '—'}
          </p>
        </div>
      </div>
      
      {/* Time Adjustment Section */}
      {isLate && isPending && showAdjust && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm font-medium text-blue-800 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Adjust Time Before Approval
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Adjusted Clock In</label>
              <Input
                type="datetime-local"
                value={adjustedClockIn}
                onChange={(e) => setAdjustedClockIn(e.target.value)}
                className="text-sm"
                placeholder="Leave empty to keep original"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Adjusted Clock Out</label>
              <Input
                type="datetime-local"
                value={adjustedClockOut}
                onChange={(e) => setAdjustedClockOut(e.target.value)}
                className="text-sm"
                placeholder="Leave empty to keep original"
              />
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Leave fields empty to approve with original times. Adjusted times will recalculate hours.
          </p>
        </div>
      )}
      
      {/* Approval Actions */}
      {isLate && isPending && (
        <div className="mt-4 flex items-center justify-between pt-3 border-t border-amber-200">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <AlertTriangle className="w-4 h-4" />
              <span>Requires approval</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-blue-600 hover:bg-blue-50 text-xs"
              onClick={() => setShowAdjust(!showAdjust)}
            >
              <Clock className="w-3 h-3 mr-1" />
              {showAdjust ? 'Hide Adjustment' : 'Adjust Hours'}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 hover:bg-red-50 border-red-200"
              onClick={() => onReject(row.attendance_id)}
              disabled={processingId === row.attendance_id}
            >
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700"
              onClick={showAdjust ? handleApproveWithAdjustment : () => onApprove(row.attendance_id)}
              disabled={processingId === row.attendance_id}
            >
              {processingId === row.attendance_id ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-1" />
              )}
              {showAdjust && (adjustedClockIn || adjustedClockOut) ? 'Approve with Adjustment' : 'Approve'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminTimesheets() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [timesheetData, setTimesheetData] = useState([]);
  const [guards, setGuards] = useState([]);
  const [sites, setSites] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filters
  const [dateRange, setDateRange] = useState({
    start: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    end: format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
  });
  const [selectedGuard, setSelectedGuard] = useState('all');
  const [selectedSite, setSelectedSite] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadTimesheet = useCallback(async () => {
    try {
      const data = await api.getDetailedTimesheet(
        dateRange.start, 
        dateRange.end, 
        selectedGuard !== 'all' ? selectedGuard : undefined,
        selectedSite !== 'all' ? selectedSite : undefined
      );
      
      // Transform nested guard data into flat timesheet rows
      let records = [];
      if (data.guards && Array.isArray(data.guards)) {
        data.guards.forEach(guard => {
          // Skip null/undefined guards (unassigned shifts)
          if (!guard.guard_id || !guard.guard_name) return;
          
          if (guard.daily_breakdown && Array.isArray(guard.daily_breakdown)) {
            guard.daily_breakdown.forEach(day => {
              records.push({
                id: `${guard.guard_id}-${day.date}`,
                guard_id: guard.guard_id,
                guard_name: guard.guard_name,
                date: day.date,
                scheduled_start: day.scheduled_start,
                scheduled_end: day.scheduled_end,
                scheduled_hours: day.scheduled_hours || 0,
                actual_start: day.actual_start,
                actual_end: day.actual_end,
                actual_hours: day.actual_hours || 0,
                status: day.status,
                is_late: day.is_late,
                late_minutes: day.late_minutes || 0,
                approval_status: day.approval_status,
              });
            });
          }
        });
      } else if (data.records && Array.isArray(data.records)) {
        records = data.records;
      }
      
      setTimesheetData(records);
    } catch (error) {
      console.error('Error loading timesheet:', error);
      setTimesheetData([]);
    }
  }, [dateRange.start, dateRange.end, selectedGuard, selectedSite]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [guardsData, sitesData, approvalsData] = await Promise.all([
        api.getGuards(),
        api.getSites(),
        api.getPendingApprovals(),
      ]);
      setGuards(Array.isArray(guardsData) ? guardsData : []);
      setSites(Array.isArray(sitesData) ? sitesData : []);
      setPendingApprovals(Array.isArray(approvalsData) ? approvalsData : []);
      
      await loadTimesheet();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [loadTimesheet]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!loading) {
      loadTimesheet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedGuard, selectedSite]);

  // Filtered data
  const filteredData = useMemo(() => {
    let data = timesheetData;
    
    // Search filter
    if (searchQuery) {
      data = data.filter(row => 
        row.guard_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.site_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Status filter
    if (statusFilter === 'late') {
      data = data.filter(row => row.is_late);
    } else if (statusFilter === 'pending') {
      data = data.filter(row => row.is_late && row.approval_status === 'pending');
    } else if (statusFilter === 'completed') {
      data = data.filter(row => row.status === 'completed' && !row.is_late);
    }
    
    return data;
  }, [timesheetData, searchQuery, statusFilter]);

  // Stats
  const stats = useMemo(() => ({
    totalScheduled: timesheetData.reduce((sum, r) => sum + (r.scheduled_hours || 0), 0),
    totalWorked: timesheetData.reduce((sum, r) => sum + (r.actual_hours || 0), 0),
    lateCount: timesheetData.filter(r => r.is_late).length,
    pendingCount: timesheetData.filter(r => r.is_late && r.approval_status === 'pending').length,
    attendanceRate: timesheetData.length > 0 
      ? (timesheetData.filter(r => r.actual_hours > 0).length / timesheetData.length) * 100
      : 0,
  }), [timesheetData]);

  const handleApproval = async (attendanceId, action, adjustments = {}) => {
    setProcessingId(attendanceId);
    try {
      await api.approveLateCheckin(attendanceId, action, '', adjustments.adjusted_clock_in, adjustments.adjusted_clock_out);
      const hasAdjustment = adjustments.adjusted_clock_in || adjustments.adjusted_clock_out;
      toast({
        title: action === 'approved' ? 'Approved' : 'Rejected',
        description: `Late check-in has been ${action}${hasAdjustment ? ' with time adjustment' : ''}`,
      });
      loadData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to process approval',
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Export date range
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportRange, setExportRange] = useState({
    start: dateRange.start,
    end: dateRange.end,
  });
  const [exportType, setExportType] = useState('csv');

  const handleExportWithRange = () => {
    if (exportType === 'csv') {
      handleExportCSVWithRange();
    } else {
      handleExportPDFWithRange();
    }
  };

  const handleExportCSVWithRange = async () => {
    try {
      const data = await api.exportTimesheetCSV(
        exportRange.start,
        exportRange.end,
        selectedGuard !== 'all' ? selectedGuard : undefined,
        selectedSite !== 'all' ? selectedSite : undefined
      );
      
      const blob = new Blob([data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || `timesheet_${exportRange.start}_to_${exportRange.end}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({ title: "Export Complete", description: "CSV downloaded successfully" });
      setShowExportDialog(false);
    } catch (error) {
      toast({ variant: "destructive", title: "Export Failed", description: error.message });
    }
  };

  const handleExportPDFWithRange = () => {
    const pdfUrl = api.getTimesheetPdfUrl(
      exportRange.start,
      exportRange.end,
      selectedGuard !== 'all' ? selectedGuard : undefined,
      selectedSite !== 'all' ? selectedSite : undefined
    );
    const token = api.getToken();
    window.open(`${pdfUrl}&token=${token}`, '_blank');
    toast({ title: "Generating PDF", description: "Your PDF will download shortly" });
    setShowExportDialog(false);
  };

  const navigateWeek = (direction) => {
    const days = direction === 'next' ? 7 : -7;
    const newStart = addDays(parseISO(dateRange.start), days);
    const newEnd = addDays(parseISO(dateRange.end), days);
    setDateRange({
      start: format(newStart, 'yyyy-MM-dd'),
      end: format(newEnd, 'yyyy-MM-dd'),
    });
  };

  const goToCurrentWeek = () => {
    setDateRange({
      start: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      end: format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
            <p className="mt-3 text-slate-500">Loading timesheets...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-timesheets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Timesheets</h1>
            <p className="text-slate-500 text-sm">Track attendance and approve late check-ins</p>
          </div>
          <Button onClick={() => { setExportRange({ start: dateRange.start, end: dateRange.end }); setShowExportDialog(true); }} className="bg-green-600 hover:bg-green-700 gap-2">
            <Download className="w-4 h-4" /> Export
          </Button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Timer className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-blue-700">{stats.totalScheduled.toFixed(0)}h</p>
                  <p className="text-xs text-blue-600">Scheduled</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-green-700">{stats.totalWorked.toFixed(0)}h</p>
                  <p className="text-xs text-green-600">Worked</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-amber-700">{stats.lateCount}</p>
                  <p className="text-xs text-amber-600">Late</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500 rounded-lg">
                  <XCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-red-700">{stats.pendingCount}</p>
                  <p className="text-xs text-red-600">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-purple-700">{stats.attendanceRate.toFixed(0)}%</p>
                  <p className="text-xs text-purple-600">Attendance</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Controls Row */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Week Navigation */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => navigateWeek('prev')}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="px-4 py-2 bg-slate-100 rounded-lg text-sm font-medium min-w-[200px] text-center">
                  {format(parseISO(dateRange.start), 'MMM d')} - {format(parseISO(dateRange.end), 'MMM d, yyyy')}
                </div>
                <Button variant="outline" size="icon" onClick={() => navigateWeek('next')}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={goToCurrentWeek}>
                  Today
                </Button>
              </div>

              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by guard name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Quick Filters */}
              <div className="flex flex-wrap gap-2">
                <FilterChip 
                  label="All" 
                  active={statusFilter === 'all'} 
                  onClick={() => setStatusFilter('all')}
                  count={timesheetData.length}
                />
                <FilterChip 
                  label="Pending Approval" 
                  active={statusFilter === 'pending'} 
                  onClick={() => setStatusFilter('pending')}
                  count={stats.pendingCount}
                />
                <FilterChip 
                  label="Late" 
                  active={statusFilter === 'late'} 
                  onClick={() => setStatusFilter('late')}
                  count={stats.lateCount}
                />
                <FilterChip 
                  label="Completed" 
                  active={statusFilter === 'completed'} 
                  onClick={() => setStatusFilter('completed')}
                />
              </div>
            </div>

            {/* Guard Quick Links */}
            {guards.length > 0 && guards.length <= 10 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Quick filter by guard</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedGuard('all')}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                      selectedGuard === 'all' 
                        ? 'bg-blue-100 text-blue-700 font-medium' 
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    All Guards
                  </button>
                  {guards.map((guard) => (
                    <button
                      key={guard.id}
                      onClick={() => setSelectedGuard(guard.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-all flex items-center gap-2 ${
                        selectedGuard === guard.id 
                          ? 'bg-blue-100 text-blue-700 font-medium' 
                          : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        selectedGuard === guard.id ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {guard.name?.[0]?.toUpperCase()}
                      </div>
                      {guard.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timesheet Cards */}
        <div className="space-y-3">
          {filteredData.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                <p className="text-lg font-medium text-slate-600">No timesheet data</p>
                <p className="text-sm text-slate-400 mt-1">
                  {searchQuery ? 'Try a different search term' : 'No records for the selected period'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredData.map((row, idx) => (
              <TimesheetCard
                key={row.id || idx}
                row={row}
                onApprove={(id, adjustments) => handleApproval(id, 'approved', adjustments)}
                onReject={(id) => handleApproval(id, 'rejected')}
                processingId={processingId}
              />
            ))
          )}
        </div>
      </div>

      {/* Export Dialog with Date Range */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-green-600" />
              Export Timesheet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">From</label>
                <Input
                  type="date"
                  value={exportRange.start}
                  onChange={(e) => setExportRange(p => ({ ...p, start: e.target.value }))}
                  data-testid="export-start-date"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">To</label>
                <Input
                  type="date"
                  value={exportRange.end}
                  onChange={(e) => setExportRange(p => ({ ...p, end: e.target.value }))}
                  data-testid="export-end-date"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">Format</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setExportType('csv')}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                    exportType === 'csv' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span className="text-sm font-medium">CSV</span>
                </button>
                <button
                  onClick={() => setExportType('pdf')}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                    exportType === 'pdf' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">PDF</span>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Cancel</Button>
            <Button onClick={handleExportWithRange} className="bg-green-600 hover:bg-green-700 gap-2">
              <Download className="w-4 h-4" />
              Download {exportType.toUpperCase()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
